import {RefObject, useCallback, useEffect, useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import {debounce} from "lodash";
import moment from "moment";

export const usePrevious = <T = undefined>(value: T): T | undefined => {
	const ref = useRef<T>();

	useEffect(() => {
		ref.current = value;
	});

	return ref.current;
};

export const useMediaQuery = (query: string) => {
	const mediaMatch = window.matchMedia(query);
	const [matches, setMatches] = useState(mediaMatch.matches);

	useEffect(() => {
		const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
		mediaMatch.addEventListener("change", handler);

		return () => {
			mediaMatch.removeEventListener("change", handler);
		};
	});

	return matches;
};

export const useURLQuery = () => new URLSearchParams(useLocation().search);

export const useWindowSize = (delay = 300) => {
	const [windowSize, setWindowSize] = useState({
		width: 0,
		height: 0,
	});

	useEffect(() => {
		function handleResize() {
			setWindowSize({
				width: window.innerWidth,
				height: window.innerHeight,
			});
		}

		const debounced = debounce(handleResize, delay);
		window.addEventListener("resize", debounced);
		handleResize();

		return () => window.removeEventListener("resize", debounced);
	}, [delay]);

	return windowSize;
};

export const useScrollTo = (ref: RefObject<HTMLElement> | undefined | null) => () => {
	const current = ref?.current;
	if (current) {
		window.scrollTo({
			top: current.getBoundingClientRect().top + window.scrollY - 20,
			behavior: "smooth",
		});
	}
};

interface IOffsetProps {
	bottom?: number;
	right?: number;
}

export const useIsRefInViewport =
	(ref: RefObject<HTMLElement> | undefined | null, offset: IOffsetProps = {}) =>
	() => {
		const current = ref?.current;
		if (!current) {
			return false;
		}

		const rect = current.getBoundingClientRect();

		const bottomOffset = rect.bottom - (offset.bottom ?? 0) > 0;
		const rightOffset = rect.right - (offset.right ?? 0) > 0;
		const leftOffset = rect.left < (window.innerWidth || document.documentElement.clientWidth);
		const topOffset = rect.top < (window.innerHeight || document.documentElement.clientHeight);

		return [topOffset, rightOffset, bottomOffset, leftOffset].every(Boolean);
	};

interface ICountdownTimer {
	days: number;
	hours: number;
	minutes: number;
	seconds: number;
	isComplete: boolean;
}

export const useTimer = (end: string) => {
	const [timer, setTimer] = useState<ICountdownTimer>({
		days: 0,
		hours: 0,
		minutes: 0,
		seconds: 0,
		isComplete: false,
	});

	const getDuration = useCallback(() => {
		const startDate = moment(Date.now());
		const endDate = moment(end);
		const y = startDate;
		const x = endDate;
		const t = x.diff(y) > 0 ? x.diff(y) : 0;
		return moment.duration(t);
	}, [end]);

	const getDays = useCallback(() => Math.floor(getDuration().asDays()), [getDuration]);
	const getHours = useCallback(() => getDuration().hours(), [getDuration]);
	const getMinutes = useCallback(() => getDuration().minutes(), [getDuration]);
	const getSeconds = useCallback(() => getDuration().seconds(), [getDuration]);
	const isComplete = useCallback(() => getDuration().milliseconds() === 0, [getDuration]);

	useEffect(() => {
		const interval = setInterval(() => {
			setTimer({
				days: getDays(),
				hours: getHours(),
				minutes: getMinutes(),
				seconds: getSeconds(),
				isComplete: isComplete(),
			});
		}, 1000);

		return () => {
			clearInterval(interval);
		};
	}, [getDays, getHours, getMinutes, getSeconds, isComplete]);

	return timer;
};
